import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  AccessRoleDef,
  builtinAccessRoles,
  emptyPermissions,
} from "@/lib/staff-permissions";

const SETTINGS_KEY = "access_roles";

const normalize = (r: any): AccessRoleDef => ({
  key: String(r?.key || ""),
  label: String(r?.label || ""),
  description: String(r?.description || ""),
  permissions: { ...emptyPermissions(), ...(r?.permissions || {}) },
  builtin: !!r?.builtin,
});

/**
 * Categorias de acesso (menus/submenus) guardadas em app_settings.
 * Se ainda não existirem, usa os presets nativos como base.
 */
export const useAccessRoles = () => {
  const [roles, setRoles] = useState<AccessRoleDef[]>(builtinAccessRoles());
  const [loading, setLoading] = useState(true);

  const fetchRoles = useCallback(async () => {
    const { data } = await supabase
      .from("app_settings")
      .select("value")
      .eq("key", SETTINGS_KEY)
      .maybeSingle();

    const builtins = builtinAccessRoles();
    if (data?.value && Array.isArray(data.value) && data.value.length > 0) {
      const stored = (data.value as any[]).map(normalize).filter((r) => r.key && r.label);
      // A categoria "admin" (acesso total) é sempre garantida
      const adminBuiltin = builtins.find((r) => r.key === "admin")!;
      const merged = stored.some((r) => r.key === "admin")
        ? stored.map((r) => (r.key === "admin" ? { ...adminBuiltin, label: r.label || adminBuiltin.label, description: r.description || adminBuiltin.description } : r))
        : [adminBuiltin, ...stored];
      setRoles(merged);
    } else {
      setRoles(builtins);
    }
    setLoading(false);
  }, []);


  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const saveRoles = useCallback(async (updated: AccessRoleDef[]) => {
    setRoles(updated);
    const { error } = await supabase
      .from("app_settings")
      .upsert({ key: SETTINGS_KEY, value: updated as any }, { onConflict: "key" });
    return error;
  }, []);

  const getRole = useCallback(
    (key: string) => roles.find((r) => r.key === key),
    [roles]
  );

  return { roles, loading, fetchRoles, saveRoles, getRole };
};
